import { useState, type FormEvent } from 'react';
import { useEvaluateContactMutation, type Evaluation } from './api.js';
import { Alert, Button, Card, SectionHeading } from './ui.js';
import { PlanSummary } from './PlanSummary.js';

/**
 * A deliberate dry run of the decision path.
 *
 * Identity resolution and the policy engine run for real; nothing is executed. The
 * server says so in its own response (`executed`, `mutatedCrm`), and this screen
 * reports what the server said rather than asserting it independently.
 */

const DECISION_COPY: Record<string, { title: string; tone: 'info' | 'success' | 'error'; body: string }> = {
  AUTO: {
    title: 'Would act automatically',
    tone: 'success',
    body: 'Confidence is high enough to act without a person, once automatic actions are enabled.',
  },
  REVIEW: {
    title: 'Needs a person',
    tone: 'info',
    body: 'The proposed action is held for someone to approve.',
  },
  /*
   * A CRM outcome, said as one.
   *
   * "Nothing to do" describes the CRM and reads as a verdict on the whole reading. A
   * message can be understood perfectly — sender, relationship, what it is about — and
   * still warrant no change to a CRM record, which is a successful classification rather
   * than an absent one. The distinction matters most for email intelligence, where most
   * messages will never justify writing anything to a CRM.
   */
  SKIP: {
    title: 'No CRM change needed',
    tone: 'info',
    body: 'This was read and understood. Nothing needs to be written to your CRM.',
  },
};

const MATCH_COPY: Record<string, string> = {
  existing_mapping: 'Already linked to a CRM record',
  exact_email: 'Matched an existing contact by email',
  exact_phone: 'Matched an existing contact by phone',
  prior_correspondence: 'Known to us, not yet linked in the CRM',
  probabilistic: 'Ambiguous — more than one plausible match',
  no_match: 'No existing match; this would be a new contact',
};

function Result({ result }: { result: Evaluation }) {
  const decision = DECISION_COPY[result.policy.decision]!;
  return (
    <div className="space-y-4">
      <Alert tone={decision.tone}>
        <span className="font-semibold">{decision.title}.</span> {decision.body}
      </Alert>

      <Card className="divide-y divide-slate-200">
        <div className="p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Who this is</h4>
          <p className="mt-1 text-sm text-slate-800">
            {MATCH_COPY[result.identity.matchTier] ?? result.identity.matchTier}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Confidence {Math.round(result.identity.confidence * 100)}%
            {result.identity.externalContactIds.length > 0
              ? ` · Keap contact ${result.identity.externalContactIds.join(', ')}`
              : ''}
            {result.identity.alternativeCount > 0
              ? ` · ${result.identity.alternativeCount} other candidate(s)`
              : ''}
          </p>
        </div>

        <div className="p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Why</h4>
          <p className="mt-1 text-sm text-slate-800">{result.policy.reason}</p>
          {result.policy.shadowDecision && result.policy.shadowDecision !== result.policy.decision ? (
            <p className="mt-1 text-xs text-slate-500">
              With automatic sending on, this would have been <strong>{result.policy.shadowDecision}</strong>.
            </p>
          ) : null}
          {result.policy.skipReason ? (
            <p className="mt-1 text-xs text-slate-500">Reason: {result.policy.skipReason.replace(/_/g, ' ')}</p>
          ) : null}
          {result.policy.reviewReasons.length > 0 ? (
            <ul className="mt-2 list-inside list-disc text-xs text-slate-500">
              {result.policy.reviewReasons.map((r) => <li key={r}>{r.replace(/_/g, ' ')}</li>)}
            </ul>
          ) : null}
        </div>

        <div className="p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Proposed action</h4>
          {result.proposedOperations.length === 0 ? (
            <p className="mt-1 text-sm text-slate-600">None.</p>
          ) : (
            <div className="mt-2">
              {/* The same reading of a plan as the card's confirm screen: values, not field names. */}
              <PlanSummary operations={result.proposedOperations} tagName={(k) => k} typeLabel={(v) => v} />
            </div>
          )}
          {result.approvedTagKeys.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500">
              No tag is approved for use yet, so no tag can be proposed. Add one in
              Settings → CRM.
            </p>
          ) : null}
        </div>
      </Card>

      {/* Reported from the server's own response, not assumed by this screen. */}
      <Alert tone="success">
        {result.mutatedCrm
          ? 'Keap was modified.'
          : 'No change has been made to Keap. Nothing was created, updated or tagged.'}
      </Alert>
    </div>
  );
}

export function TestContact() {
  const [form, setForm] = useState({
    givenName: '', familyName: '', email: '', phone: '', company: '', jobTitle: '',
  });
  const [evaluate, { data, isLoading, error, reset }] = useEvaluateContactMutation();
  const [failure, setFailure] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFailure(null);
    reset();
    try {
      await evaluate({
        givenName: form.givenName || undefined,
        familyName: form.familyName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        company: form.company || undefined,
        jobTitle: form.jobTitle || undefined,
      }).unwrap();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setFailure(
        status === 404
          ? 'Evaluation needs a working Keap connection, because it looks the person up there.'
          : status === 400
            ? 'Enter a valid email address or phone number.'
            : 'The evaluation could not be completed.',
      );
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <SectionHeading
          title="Test contact"
          hint="Run one contact through identity matching and the decision rules. Nothing is written to Keap."
        />
        <Card className="p-5">
          <form onSubmit={submit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-slate-700">First name</span>
                <input value={form.givenName} onChange={set('givenName')}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
              </label>
              <label className="block text-sm">
                <span className="text-slate-700">Last name</span>
                <input value={form.familyName} onChange={set('familyName')}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
              </label>
            </div>
            <label className="block text-sm">
              <span className="text-slate-700">Email</span>
              <input type="email" value={form.email} onChange={set('email')}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700">Phone</span>
              <input value={form.phone} onChange={set('phone')}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-slate-700">Company</span>
                <input value={form.company} onChange={set('company')}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
              </label>
              <label className="block text-sm">
                <span className="text-slate-700">Job title</span>
                <input value={form.jobTitle} onChange={set('jobTitle')}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Looking the contact up reads from Keap. It writes nothing.
            </p>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Evaluating…' : 'Evaluate'}
            </Button>
          </form>
        </Card>
      </div>

      <div>
        <SectionHeading title="Result" />
        {failure || (error && !data) ? <Alert tone="error">{failure ?? 'The evaluation failed.'}</Alert> : null}
        {!data && !failure && !isLoading ? (
          <p className="text-sm text-slate-500">
            Enter a contact and choose Evaluate. You will see who we think it is, what the
            rules decide, and exactly what would be sent to Keap — without sending it.
          </p>
        ) : null}
        {data ? <Result result={data} /> : null}
      </div>
    </div>
  );
}
